import React, { useState, useEffect } from "react";
import { Card, Headline, Paragraph, Loading, Button } from "_styleguide";
import { useNavigate } from "react-router-dom";
import { useLead } from "_contexts/useLead";
import { flatMoney } from "_assets/js/helpers";
import {
  previewData,
  getApprovedTradelines,
  getEligibleBalance,
} from "./_helpers";
import CreditItem from "./_components/CreditItem/CreditItem";
import CreditAlert from "./_components/CreditAlert/CreditAlert";
import { useApolloClient } from "@apollo/client";
import { getApplicationTradelines } from "_graphql/queries";
import { saveTradelines } from "_graphql/mutations";
import { useSettings } from "_contexts/useSettings";
import { message } from "@centrate-io/barn";
import { isDarkColor } from "_helpers/colors";
import { useStats } from "_contexts/useStats";
import "./CreditSelect.scss";

function CreditSelect(props) {
  const client = useApolloClient();
  const navigate = useNavigate();
  const { lead, reload } = useLead();
  const { isPreview } = useSettings();
  const [tradelines, setTradelines] = useState([]);
  const [eligibleBalance, setEligibleBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { settings } = useSettings();
  const { updateStats } = useStats();
  const pageSettings = props.settings?.settings;

  const fetchTradelines = async () => {
    const response = await client.query({
      query: getApplicationTradelines,
      variables: {
        application_id: lead?.id,
      },
    });
    const eligibleTradelines = getApprovedTradelines(
      response.data?.getCurrentTradelines || []
    );
    const eligibleBalance = getEligibleBalance(eligibleTradelines);
    setTradelines(eligibleTradelines);
    setEligibleBalance(eligibleBalance);
    setLoading(false);
  };

  const updateTradelines = async () => {
    try {
      setSaving(true);
      await client.mutate({
        variables: {
          tradelines: tradelines?.filter((t) => t.selected).map((t) => t.id),
          application_id: lead?.id,
        },
        mutation: saveTradelines,
      });
      await reload();
      await updateStats({
        application_id: lead.id,
        self_enrollment_id: settings.id,
        page: props?.settings?.name,
        drop_off_page: props?.settings?.next || props?.settings?.name,
      });
      navigate(`/${props?.settings?.next}`);
    } catch (err) {
      message.error("Issue updating tradelines, try again");
    } finally {
      setSaving(false);
    }
  };

  const toggleTradeline = (tradelineId) => {
    const updatedTradelines = tradelines.map((tradeline) =>
      tradeline.id === tradelineId
        ? { ...tradeline, selected: !tradeline.selected }
        : tradeline
    );
    setTradelines(updatedTradelines);
  };

  useEffect(() => {
    if (!isPreview) {
      fetchTradelines();
    } else {
      setTradelines(previewData);
      setEligibleBalance(getEligibleBalance(previewData));
      setLoading(false);
    }
  }, []);

  let actualTitle = pageSettings.title?.replace(
    "{{amount}}",
    flatMoney(eligibleBalance)
  );

  let actualVerifyTitle = pageSettings.verify_title?.replace(
    "{{amount}}",
    flatMoney(eligibleBalance)
  );

  const selectedAmount = tradelines?.reduce((acc, t) => {
    if (t.selected) {
      return acc + parseInt(t.tradeline.balanceAmount);
    }
    return acc;
  }, 0);

  const primaryColor = settings?.primary_color || "#0f766e";
  const primaryFontColor = isDarkColor(primaryColor) ? "#fff" : "#000";

  return loading ? (
    <Loading />
  ) : (
    <div id="credit-select">
      <Card>
        <div className="credit-select-body">
          <div className="apply-form">
            <Headline>{actualTitle}</Headline>
            {pageSettings.verify_title ? (
              <Paragraph
                opacity={0.6}
                align="left"
                size="14px"
                dangerouslySetInnerHTML={{
                  __html: actualVerifyTitle,
                }}
              />
            ) : null}
          </div>
          <div className="credit-select-container">
            <CreditAlert
              pageSettings={pageSettings}
              eligibleBalance={eligibleBalance}
              selectedAmount={selectedAmount}
            />
            {tradelines?.map((item) => (
              <CreditItem
                key={`credit-select-item-${item.id}`}
                item={item}
                toggleTradeline={toggleTradeline}
              />
            ))}
          </div>
        </div>
      </Card>
      <div className="credit-select-actions">
        <Button
          block
          size="large"
          style={{ background: primaryColor, color: primaryFontColor }}
          onClick={updateTradelines}
          loading={saving}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}

export default CreditSelect;
